import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { MILE_TYPES } from "../TariffPlans/mileTypes";

/**
 * Настройки партнёра сразу по всем плечам: на каждом — тарифная карта и процент.
 *
 * Процент имеет смысл задавать и там, где выбрана карта: приоритет у карты, но
 * если направления в ней нет, плечо считается обычным тарифом — и тогда процент
 * применяется. Без этого партнёр молча терял бы скидку ровно на тех
 * направлениях, которых не внёс в карту.
 *
 * Пустое плечо (ни процента, ни карты) — это отсутствие настроек, и при
 * сохранении такая строка удаляется, а не пишется пустой.
 */
const RuleDialog = ({ visible, onHide, onSubmit, loading, rule, orgs, plans }) => {
  const [orgId, setOrgId] = React.useState(null);
  const [miles, setMiles] = React.useState({});
  const [trustRequestPercent, setTrustRequestPercent] = React.useState(false);
  const [active, setActive] = React.useState(true);

  const isEdit = Boolean(rule);

  React.useEffect(() => {
    if (!visible) return;
    setOrgId(rule?.orgId ?? null);
    setMiles(rule?.miles ?? {});
    setTrustRequestPercent(rule?.trustRequestPercent ?? false);
    setActive(rule?.active ?? true);
  }, [visible, rule]);

  const setMile = (mileType, patch) =>
    setMiles((prev) => ({ ...prev, [mileType]: { ...(prev[mileType] ?? {}), ...patch } }));

  const invalidPercent = MILE_TYPES.some(({ value }) => {
    const percent = miles[value]?.percent;
    return percent != null && (percent < 0 || percent >= 100);
  });

  const nothingSet =
    !trustRequestPercent &&
    MILE_TYPES.every(({ value }) => miles[value]?.percent == null && !miles[value]?.tariffPlanId);

  const canSave = Boolean(orgId) && !invalidPercent && !nothingSet;

  const handleSave = () => {
    if (!canSave) return;
    onSubmit({
      orgId,
      trustRequestPercent,
      active,
      miles: MILE_TYPES.reduce((acc, { value }) => {
        acc[value] = {
          percent: miles[value]?.percent ?? null,
          tariffPlanId: miles[value]?.tariffPlanId ?? null,
        };
        return acc;
      }, {}),
    });
  };

  const footer = (
    <div>
      <Button label="Отмена" icon="pi pi-times" className="p-button-text" onClick={onHide} />
      <Button label="Сохранить" icon="pi pi-check" loading={loading} disabled={!canSave} onClick={handleSave} />
    </div>
  );

  const orgOptions = (orgs || []).map((o) => ({ label: o.name, value: o.id }));
  const plansForMile = (mileType) =>
    (plans || [])
      .filter((p) => p.mileType === mileType)
      .map((p) => ({ label: p.isActive ? p.name : `${p.name} (выключена)`, value: p.id }));

  return (
    <Dialog header={isEdit ? `Партнёр: ${rule.companyName || rule.orgId}` : "Новые настройки партнёра"}
            visible={visible} style={{ width: "760px" }} modal onHide={onHide} footer={footer}>
      <div className="p-fluid p-formgrid p-grid">
        <div className="p-field p-col-12">
          <label>Организация</label>
          <Dropdown value={orgId} options={orgOptions} filter showClear
                    placeholder="Выберите организацию" disabled={isEdit}
                    onChange={(e) => setOrgId(e.value)} />
        </div>

        <div className="p-col-12">
          <Message severity="info" style={{ display: "block", marginBottom: 8 }}
                   text="Приоритет у тарифной карты: покрыла она направление — цена из карты, скидка не применяется. Направления, которого в карте нет, считаются обычным тарифом — там и сработает процент." />
        </div>

        {MILE_TYPES.map(({ value, label }) => (
          <React.Fragment key={value}>
            <div className="p-field p-col-12 p-md-4">
              <label>{label}</label>
              <Dropdown value={miles[value]?.tariffPlanId ?? null} options={plansForMile(value)}
                        showClear filter placeholder="Стандартный тариф"
                        onChange={(e) => setMile(value, { tariffPlanId: e.value ?? null })} />
            </div>
            <div className="p-field p-col-12 p-md-3">
              <label>Скидка, %</label>
              <InputNumber value={miles[value]?.percent ?? null} mode="decimal"
                           minFractionDigits={0} maxFractionDigits={2} min={0} max={99.99}
                           placeholder="без скидки"
                           onValueChange={(e) => setMile(value, { percent: e.value ?? null })} />
            </div>
            <div className="p-col-12 p-md-5" style={{ alignSelf: "center", color: "#6b7280" }}>
              {miles[value]?.tariffPlanId
                ? "Цена по карте; процент сработает на направлениях, которых в ней нет."
                : "Обычный расчёт со скидкой, если процент задан."}
            </div>
          </React.Fragment>
        ))}

        <div className="p-field p-col-12 p-md-6">
          <label>Брать процент из запроса</label>
          <div>
            <InputSwitch checked={trustRequestPercent} onChange={(e) => setTrustRequestPercent(e.value)} />
          </div>
          <small className="p-d-block">
            Только для организации, под ключом которой работает калькулятор slq.kz:
            так доезжает дисконт юрлица. Партнёрам не включать.
          </small>
        </div>

        <div className="p-field p-col-12 p-md-6">
          <label>Настройки включены</label>
          <div>
            <InputSwitch checked={active} onChange={(e) => setActive(e.value)} />
          </div>
          <small className="p-d-block">Выключает партнёра целиком, все его плечи разом.</small>
        </div>

        {nothingSet && (
          <div className="p-col-12">
            <Message severity="warn"
                     text="Настройки ничего не делают: задайте процент или карту хотя бы на одном плече либо разрешите брать процент из запроса." />
          </div>
        )}
        {invalidPercent && (
          <div className="p-col-12">
            <Message severity="error" text="Процент должен быть в диапазоне от 0 до 100." />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default RuleDialog;
